import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';

import {
  DensityUom,
  DimsUom,
  freightClassesByDensity,
  VolumeUom,
  WeightUom,
} from '@rygen/rygen-ui/model';

import { LineItem } from '@/model';

type LineItemMeasurements = {
  suggestedFreightClass: ComputedRef<LineItem['freightClass']>;
};

export const useLineItemMeasurements = (
  lineItem: Ref<LineItem>,
): LineItemMeasurements => {
  const CONTAINER_WIDTH = 96;
  const CONTAINER_HEIGHT = 96;

  const _suggestedFreightClass: Ref<LineItem['freightClass']> = ref(undefined);

  const suggestedFreightClass = computed(() => _suggestedFreightClass.value);

  const getFreightClassSuggestion = (
    density?: number,
  ): LineItem['freightClass'] => {
    if (density === undefined) {
      return;
    }

    const match = Object.entries(freightClassesByDensity)
      .sort((a, b) => {
        const aThreshold = parseFloat(a[0]);
        const bThreshold = parseFloat(b[0]);
        if (aThreshold > bThreshold) {
          return -1;
        } else if (bThreshold > aThreshold) {
          return 1;
        } else {
          return 0;
        }
      })
      .find(([thresholdKey, _]) => {
        const threshold = Number.parseFloat(thresholdKey);
        return density >= threshold;
      });

    return match?.[1];
  };

  const calculateDensityAndFreightClass = () => {
    if (
      lineItem.value.volume === undefined ||
      lineItem.value.weight === undefined
    ) {
      lineItem.value.density = 0;
      lineItem.value.densityUom = DensityUom.POUND_PER_CUBIC_FOOT;
      return;
    }

    const weight = WeightUom.convert(
      lineItem.value.weight,
      lineItem.value.weightUom,
      WeightUom.POUND,
    );

    const volume = VolumeUom.convert(
      lineItem.value.volume,
      lineItem.value.volumeUom,
      VolumeUom.CUBIC_FOOT,
    );

    lineItem.value.density = Math.round((weight / volume) * 10) / 10;
    lineItem.value.densityUom = DensityUom.POUND_PER_CUBIC_FOOT;

    _suggestedFreightClass.value = getFreightClassSuggestion(
      lineItem.value.density,
    );

    if (!lineItem.value.freightClass) {
      lineItem.value.freightClass = _suggestedFreightClass.value;
    }
  };

  const calculateLinearFeet = () => {
    if (
      lineItem.value.quantity === undefined ||
      lineItem.value.lengthDim === undefined
    ) {
      lineItem.value.linear = 0;
      return;
    }

    const length = DimsUom.convert(
      lineItem.value.lengthDim,
      lineItem.value.dimensionalUom,
      DimsUom.INCH,
    );
    const width = DimsUom.convert(
      lineItem.value.widthDim || 0.0,
      lineItem.value.dimensionalUom,
      DimsUom.INCH,
    );
    const height = DimsUom.convert(
      lineItem.value.heightDim || 0.0,
      lineItem.value.dimensionalUom,
      DimsUom.INCH,
    );
    const palletCount = lineItem.value.quantity;

    const widthAsWidthAdjustmentFactor = Math.max(
      Math.floor(CONTAINER_WIDTH / width),
      1,
    );
    const lengthAsWidthAdjustmentFactor = Math.max(
      Math.floor(CONTAINER_WIDTH / length),
      1,
    );

    let effectiveWidth: number;
    if (width > CONTAINER_WIDTH && length > CONTAINER_WIDTH) {
      effectiveWidth = Math.min(length, width);
    } else if (width > CONTAINER_WIDTH) {
      effectiveWidth = length;
    } else if (length > CONTAINER_WIDTH) {
      effectiveWidth = width;
    } else if (widthAsWidthAdjustmentFactor === lengthAsWidthAdjustmentFactor) {
      effectiveWidth = Math.max(length, width);
    } else {
      effectiveWidth = Math.min(length, width);
    }

    let effectiveLength: number;
    if (lineItem.value.turnable) {
      effectiveLength = effectiveWidth === width ? length : width;
    } else {
      effectiveLength = length;
    }
    const adjustmentFactor = Math.max(
      Math.floor(CONTAINER_WIDTH / effectiveWidth),
      1,
    );
    let stackFactor = 1.0;
    if (lineItem.value.stackable) {
      stackFactor = Math.max(Math.floor(CONTAINER_HEIGHT / height), 1);
    }
    const palletsPerRow = adjustmentFactor * stackFactor;
    const rowFactor = Math.ceil(palletCount / palletsPerRow);

    let linearFeet = (effectiveLength * rowFactor) / 12;

    if (linearFeet < 1) {
      linearFeet = 1.0;
    }

    lineItem.value.linear = Math.ceil(linearFeet);
    lineItem.value.linearUom = DimsUom.FOOT;
  };

  watch(() => lineItem.value.volume, calculateDensityAndFreightClass);
  watch(() => lineItem.value.weight, calculateDensityAndFreightClass);
  watch(() => lineItem.value.weightUom, calculateDensityAndFreightClass);
  watch(
    () => lineItem.value.volumeUom,
    () => {
      calculateDensityAndFreightClass();
      calculateLinearFeet();
    },
  );
  watch(
    () => lineItem.value.quantity,
    () => {
      calculateDensityAndFreightClass();
      calculateLinearFeet();
    },
  );
  watch(() => lineItem.value.lengthDim, calculateLinearFeet);
  watch(() => lineItem.value.widthDim, calculateLinearFeet);
  watch(() => lineItem.value.heightDim, calculateLinearFeet);
  watch(() => lineItem.value.stackable, calculateLinearFeet);
  watch(() => lineItem.value.turnable, calculateLinearFeet);
  watch(
    () => lineItem.value.hazmatData.hazmat,
    (newVal: boolean) => {
      if (!newVal) {
        lineItem.value.hazmatData.unNumber = undefined;
        lineItem.value.hazmatData.hazardDescription = undefined;
        lineItem.value.hazmatData.hazardClass = undefined;
        lineItem.value.hazmatData.packingGroup = undefined;
      }
    },
  );

  return {
    suggestedFreightClass,
  };
};
